import { z } from "zod";
import { PERSON_NAME_REGEX } from "@/lib/validators/name";

export const ContactPayloadSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .regex(PERSON_NAME_REGEX, "Name must contain only letters"),
  email: z.string().email(),
  phone: z.string().optional(),
  message: z.string().trim().min(10, "Message must be at least 10 characters"),
  source: z.enum(["contact", "home", "volunteer"]).optional(),
});

export type ContactPayload = z.infer<typeof ContactPayloadSchema>;

export const NewsletterPayloadSchema = z.object({
  email: z.string().email(),
});

export type NewsletterPayload = z.infer<typeof NewsletterPayloadSchema>;

export const NewsletterUnsubscribePayloadSchema = z.object({
  email: z.string().email(),
});

export type NewsletterUnsubscribePayload = z.infer<
  typeof NewsletterUnsubscribePayloadSchema
>;

export const AdminLoginStatSchema = z.object({
  id: z.string(),
  label: z.string(),
  value: z.number(),
});

export type AdminLoginStat = z.infer<typeof AdminLoginStatSchema>;

export const AdminLoginContentSchema = z.object({
  foundationName: z.string(),
  appVersion: z.string(),
  tagline: z.string(),
  headline: z.string(),
  subtext: z.string(),
  image: z.string(),
  imageAlt: z.string(),
  formTitle: z.string(),
  formSubtitle: z.string(),
  trustHeading: z.string(),
  trustBody: z.string(),
  hopeMessage: z.string(),
  pillars: z.array(z.string()),
  stats: z.array(AdminLoginStatSchema),
});

export type AdminLoginContent = z.infer<typeof AdminLoginContentSchema>;

export const StatsSchema = z.object({
  yearsOfService: z.number(),
  projectsCompleted: z.number(),
  livesImpacted: z.number(),
  volunteers: z.number().optional(),
  communities: z.number().optional(),
  raisedThisYear: z.number(),
  directToProgramsPercent: z.number().optional(),
});

export type Stats = z.infer<typeof StatsSchema>;

export const EventSchema = z.object({
  id: z.union([z.string(), z.number()]),
  title: z.string(),
  date: z.string(),
  description: z.string(),
  status: z.string(),
  items: z.string().nullable().optional(),
  images: z.array(z.string()).nullable().optional(),
  heroImage: z.string().nullable().optional(),
  featured: z.boolean().optional(),
});

export type Event = z.infer<typeof EventSchema>;

export const TestimonialSchema = z.object({
  id: z.string(),
  quote: z.string(),
  name: z.string(),
  role: z.string(),
  image: z.string(),
});

export type Testimonial = z.infer<typeof TestimonialSchema>;

export const HomeSectionCopySchema = z.object({
  eyebrow: z.string(),
  title: z.string(),
  subtitle: z.string(),
});

export type HomeSectionCopy = z.infer<typeof HomeSectionCopySchema>;

export const HomeHeroCtaSchema = z.object({
  label: z.string(),
  href: z.string(),
});

export const HomeHeroContentSchema = z.object({
  eyebrow: z.string(),
  headingLines: z.array(z.string()).min(1),
  subtitle: z.string(),
  heroImage: z.string(),
  heroImageAlt: z.string(),
  primaryCta: HomeHeroCtaSchema,
  secondaryCta: HomeHeroCtaSchema,
});

export type HomeHeroContent = z.infer<typeof HomeHeroContentSchema>;

export const HomeImpactMetricSchema = z.object({
  id: z.string(),
  label: z.string(),
  value: z.number(),
  suffix: z.string(),
  featured: z.boolean().optional(),
});

export type HomeImpactMetric = z.infer<typeof HomeImpactMetricSchema>;

export const HomeImpactContentSchema = z.object({
  eyebrow: z.string(),
  title: z.string(),
  subtitle: z.string(),
  cta: z.string(),
  ctaHref: z.string(),
  metrics: z.array(HomeImpactMetricSchema),
});

export type HomeImpactContent = z.infer<typeof HomeImpactContentSchema>;

export const HomeAboutTimelineItemSchema = z.object({
  year: z.string(),
  title: z.string(),
  description: z.string(),
});

export type HomeAboutTimelineItem = z.infer<typeof HomeAboutTimelineItemSchema>;

export const HomeAboutStoryContentSchema = z.object({
  eyebrow: z.string(),
  title: z.string(),
  missionImage: z.string(),
  missionImageAlt: z.string(),
  missionStatement: z.string(),
  highlightKeywords: z.array(z.string()),
  timeline: z.array(HomeAboutTimelineItemSchema),
  ctaLabel: z.string(),
  ctaHref: z.string(),
});

export type HomeAboutStoryContent = z.infer<typeof HomeAboutStoryContentSchema>;

export const HomeDonateSectionSchema = z.object({
  title: z.string(),
  subtitle: z.string(),
  cta: z.string(),
  ctaHref: z.string(),
});

export type HomeDonateSection = z.infer<typeof HomeDonateSectionSchema>;

export const HomeFeaturedProgramsContentSchema = z.object({
  eyebrow: z.string(),
  title: z.string(),
  subtitle: z.string(),
  slugs: z.array(z.string()),
  ctaLabel: z.string(),
  ctaHref: z.string(),
});

export type HomeFeaturedProgramsContent = z.infer<
  typeof HomeFeaturedProgramsContentSchema
>;

export const HomeMiniGalleryImageSchema = z.object({
  id: z.string(),
  src: z.string(),
  alt: z.string(),
  sortOrder: z.number(),
});

export type HomeMiniGalleryImage = z.infer<typeof HomeMiniGalleryImageSchema>;

export const HomeMiniGalleryContentSchema = z.object({
  eyebrow: z.string(),
  title: z.string(),
  subtitle: z.string(),
  ctaLabel: z.string(),
  ctaHref: z.string(),
  images: z.array(HomeMiniGalleryImageSchema),
});

export type HomeMiniGalleryContent = z.infer<typeof HomeMiniGalleryContentSchema>;

export const HomeContentSchema = z.object({
  hero: HomeHeroContentSchema,
  eventsSection: HomeSectionCopySchema,
  testimonialsSection: HomeSectionCopySchema,
  impact: HomeImpactContentSchema,
  aboutStory: HomeAboutStoryContentSchema,
  donateSection: HomeDonateSectionSchema,
  featuredPrograms: HomeFeaturedProgramsContentSchema,
  miniGallery: HomeMiniGalleryContentSchema,
});

export type HomeContent = z.infer<typeof HomeContentSchema>;

export const AboutCtaSchema = z.object({
  label: z.string(),
  href: z.string(),
});

export const AboutStatSchema = z.object({
  value: z.string(),
  label: z.string(),
});

export const AboutHeroContentSchema = z.object({
  eyebrow: z.string(),
  title: z.string(),
  accentWord: z.string(),
  subtitle: z.string(),
  imageSrc: z.string(),
  imageAlt: z.string(),
  primaryCta: AboutCtaSchema,
  secondaryCta: AboutCtaSchema,
  stats: z.array(AboutStatSchema),
});

export type AboutHeroContent = z.infer<typeof AboutHeroContentSchema>;

export const AboutIntroContentSchema = z.object({
  headline: z.string(),
  lead: z.string(),
  paragraphs: z.array(z.string()),
  founderCard: z.object({
    name: z.string(),
    role: z.string(),
    quote: z.string(),
    imageSrc: z.string(),
    imageAlt: z.string(),
    template: z.string(),
  }),
});

export type AboutIntroContent = z.infer<typeof AboutIntroContentSchema>;

export type AboutFounderCard = AboutIntroContent["founderCard"];

export const AboutPurposeBlockSchema = z.object({
  title: z.string(),
  description: z.string(),
});

export const AboutPurposeContentSchema = z.object({
  vision: AboutPurposeBlockSchema,
  mission: AboutPurposeBlockSchema,
});

export type AboutPurposeContent = z.infer<typeof AboutPurposeContentSchema>;

export const AboutStoryTimelineItemSchema = z.object({
  step: z.string(),
  title: z.string(),
  description: z.string(),
});

export const AboutStoryContentSchema = z.object({
  overviewTitle: z.string(),
  overviewParagraphs: z.array(z.string()),
  timeline: z.array(AboutStoryTimelineItemSchema),
  quote: z.string(),
  closing: z.string(),
});

export type AboutStoryContent = z.infer<typeof AboutStoryContentSchema>;

export const AboutValueItemSchema = z.object({
  title: z.string(),
  description: z.string(),
  icon: z.string(),
});

export const AboutValuesContentSchema = z.object({
  items: z.array(AboutValueItemSchema),
  footer: z.string(),
});

export type AboutValuesContent = z.infer<typeof AboutValuesContentSchema>;

export const AboutClosingCtaContentSchema = z.object({
  title: z.string(),
  description: z.string(),
  primary: AboutCtaSchema,
  secondary: AboutCtaSchema,
});

export type AboutClosingCtaContent = z.infer<typeof AboutClosingCtaContentSchema>;

export const AboutGalleryImageSchema = z.object({
  id: z.string(),
  src: z.string(),
  alt: z.string(),
  sortOrder: z.number(),
});

export const AboutGalleryContentSchema = z.object({
  title: z.string(),
  subtitle: z.string(),
  images: z.array(AboutGalleryImageSchema),
});

export type AboutGalleryContent = z.infer<typeof AboutGalleryContentSchema>;

export const AboutLeadershipMemberSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string(),
  bio: z.string(),
  image: z.string(),
  sortOrder: z.number(),
  published: z.boolean(),
});

export type AboutLeadershipMember = z.infer<typeof AboutLeadershipMemberSchema>;

export const AboutLeadershipContentSchema = z.object({
  title: z.string(),
  accentWord: z.string(),
  subtitle: z.string(),
  members: z.array(AboutLeadershipMemberSchema),
});

export type AboutLeadershipContent = z.infer<typeof AboutLeadershipContentSchema>;

export const AboutContentSchema = z.object({
  hero: AboutHeroContentSchema,
  intro: AboutIntroContentSchema,
  purpose: AboutPurposeContentSchema,
  story: AboutStoryContentSchema,
  values: AboutValuesContentSchema,
  closingCta: AboutClosingCtaContentSchema,
  gallery: AboutGalleryContentSchema,
  leadership: AboutLeadershipContentSchema,
});

export type AboutContent = z.infer<typeof AboutContentSchema>;

export const LegalSectionSchema = z.object({
  heading: z.string(),
  body: z.string(),
});

export type LegalSection = z.infer<typeof LegalSectionSchema>;

export const LegalDocumentSchema = z.object({
  title: z.string(),
  lastUpdated: z.string(),
  sections: z.array(LegalSectionSchema),
});

export type LegalDocument = z.infer<typeof LegalDocumentSchema>;

export const LegalContentSchema = z.object({
  privacyPolicy: LegalDocumentSchema,
  termsOfService: LegalDocumentSchema,
});

export type LegalContent = z.infer<typeof LegalContentSchema>;

export const ProgramStatSchema = z.object({
  value: z.string(),
  label: z.string(),
});

export const ProgramItemSchema = z.object({
  title: z.string(),
  desc: z.string(),
});

export const ProgramStrategyItemSchema = z.object({
  number: z.string(),
  title: z.string(),
  desc: z.string(),
});

export const ProgramApplyStepSchema = z.object({
  title: z.string(),
  description: z.string(),
});

export const CmsProgramSchema = z.object({
  slug: z.string(),
  title: z.string(),
  shortDescription: z.string(),
  features: z.array(z.string()),
  image: z.string(),
  hero: z.object({
    headline: z.string(),
    accent: z.string(),
    subtext: z.string(),
  }),
  overview: z.object({
    title: z.string(),
    icon: z.string(),
    description: z.string(),
    stats: z.array(ProgramStatSchema),
    features: z.array(z.string()),
  }),
  challenges: z.object({
    title: z.string(),
    icon: z.string(),
    items: z.array(ProgramItemSchema),
  }),
  strategy: z.object({
    title: z.string(),
    icon: z.string(),
    items: z.array(ProgramStrategyItemSchema),
  }),
  seo: z.object({
    title: z.string(),
    description: z.string(),
    ogImage: z.string(),
  }),
  apply: z
    .object({
      title: z.string(),
      intro: z.string(),
      steps: z.array(ProgramApplyStepSchema),
      note: z.string().optional(),
    })
    .nullish(),
});

export type CmsProgram = z.infer<typeof CmsProgramSchema>;

export const ProgramsContentSchema = z.object({
  programs: z.array(CmsProgramSchema),
});

export type ProgramsContent = z.infer<typeof ProgramsContentSchema>;

export const ImpactCategoryContentSchema = z.object({
  id: z.string(),
  slug: z.string(),
  summary: z.string(),
  programs: z.array(z.string()),
  image: z.string(),
});

export type ImpactCategoryContent = z.infer<typeof ImpactCategoryContentSchema>;

export const ImpactContentSchema = z.object({
  hero: z.object({
    eyebrow: z.string(),
    title: z.string(),
    accentWord: z.string(),
    subtitle: z.string(),
    imageSrc: z.string(),
    imageAlt: z.string(),
  }),
  categories: z.array(ImpactCategoryContentSchema),
  gallery: AboutGalleryContentSchema,
});

export type ImpactContent = z.infer<typeof ImpactContentSchema>;

export const RecentDonorSchema = z.object({
  name: z.string(),
  amount: z.number(),
  createdAt: z.string(),
});

export type RecentDonor = z.infer<typeof RecentDonorSchema>;

export const DonorInfoSchema = z.object({
  firstName: z
    .string()
    .min(2, "First name must be at least 2 characters")
    .regex(PERSON_NAME_REGEX, "First name must contain only letters"),
  lastName: z
    .string()
    .min(2, "Last name must be at least 2 characters")
    .regex(PERSON_NAME_REGEX, "Last name must contain only letters"),
  email: z.string().email("Enter a valid email address"),
  phone: z.string().regex(/^[6-9]\d{9}$/, "Invalid 10-digit mobile number"),
  pan: z.string().optional(),
  address: z.string().optional(),
});

export type DonorInfo = z.infer<typeof DonorInfoSchema>;

export const CreateOrderPayloadSchema = z.object({
  amount: z.number().positive(),
  currency: z.literal("INR").default("INR"),
  category: z.string(),
  type: z.literal("one-time"),
  donationSource: z
    .enum(["individual", "corporate", "csr", "events", "online"])
    .default("online"),
  donor: DonorInfoSchema,
  emailVerificationToken: z.string(),
  mobileVerificationToken: z.string(),
});

export type CreateOrderPayload = z.infer<typeof CreateOrderPayloadSchema>;

export const CreateOrderResponseSchema = z.object({
  orderId: z.string(),
  amount: z.number(),
  currency: z.string(),
  razorpayKeyId: z.string(),
});

export type CreateOrderResponse = z.infer<typeof CreateOrderResponseSchema>;

export const SendOtpPayloadSchema = z.object({
  email: z.string().email(),
  phone: z.string().regex(/^[6-9]\d{9}$/, "Invalid 10-digit mobile number"),
  name: z.string().min(1),
  channel: z.enum(["email", "mobile"]),
});

export type SendOtpPayload = z.infer<typeof SendOtpPayloadSchema>;

export const VerifyOtpPayloadSchema = z.object({
  email: z.string().email(),
  phone: z.string().regex(/^[6-9]\d{9}$/, "Invalid 10-digit mobile number"),
  otp: z.string().length(6),
  channel: z.enum(["email", "mobile"]),
});

export type VerifyOtpPayload = z.infer<typeof VerifyOtpPayloadSchema>;

export const VerifyOtpResponseSchema = z.object({
  success: z.boolean(),
  verificationToken: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
});

export type VerifyOtpResponse = z.infer<typeof VerifyOtpResponseSchema>;

export const VerifyPaymentPayloadSchema = z.object({
  razorpay_order_id: z.string(),
  razorpay_payment_id: z.string(),
  razorpay_signature: z.string(),
});

export type VerifyPaymentPayload = z.infer<typeof VerifyPaymentPayloadSchema>;

export const VerifyPaymentResponseSchema = z.object({
  success: z.boolean(),
  donationId: z.string().optional(),
  downloadToken: z.string().optional(),
  successToken: z.string().optional(),
  receiptDownloadUrl: z.string().optional(),
  certificateDownloadUrl: z.string().optional(),
});

export type VerifyPaymentResponse = z.infer<typeof VerifyPaymentResponseSchema>;

/** Donation summary for success page; amount is in rupees (INR). */
export const DonationStatusSchema = z.object({
  donationId: z.string(),
  amount: z.number(),
  currency: z.string(),
  category: z.string(),
  paidAt: z.string(),
  receiptDownloadUrl: z.string().nullable().optional(),
  certificateDownloadUrl: z.string().nullable().optional(),
  emailSentAt: z.string().nullable().optional(),
  email: z
    .union([z.string().email(), z.literal("")])
    .nullable()
    .optional(),
});

export type DonationStatus = z.infer<typeof DonationStatusSchema>;

export const SendEmailCustomPayloadSchema = z.object({
  email: z.string().email(),
  token: z.string(),
});

export type SendEmailCustomPayload = z.infer<typeof SendEmailCustomPayloadSchema>;
